import * as Tone from 'tone';
import EventBus from '@/components/EventBus.js';

class Synth {
  constructor() {
    this.midiStarted = false;
    this.oscVOct = 'C3'
  }

  applyOptions(connectors, options) {
    this.rebuild(options)
    connectors.forEach(connector => {
      this.connect(connector[0], connector[1])
    })
  }

  rebuild(options) {
    if (!this.midiStarted) {
      this.startMidi();
      this.midiStarted = true;
    }

    if (this.oscSaw) {
      this.oscSaw.dispose();
    }
    this.oscSaw = new Tone.FatOscillator(this.oscVOct, 'sawtooth').start();

    if (this.oscSquare) {
      this.oscSquare.dispose()
    }
    this.oscSquare = new Tone.Oscillator(this.oscVOct, 'square8').start();

    if (this.oscSine) {
      this.oscSine.dispose();
    }
    this.oscSine = new Tone.Oscillator(this.oscVOct, 'sine2').start();

    if (this.amp) {
      this.amp.dispose();
    }
    this.amp = new Tone.Gain(0);

    if (this.adsr) {
      this.adsr.dispose();
    }

    this.adsr = new Tone.Envelope({
      attack: options.attack,
      decay: options.decay,
      sustain: options.sustain,
      release: options.release
    });

    if (this.filter) {
      this.filter.dispose();
    }
    this.filter = new Tone.Filter(options.cutoff, 'lowpass');

    if (this.chorus) {
      this.chorus.dispose()
    }
    this.chorus = new Tone.Chorus(options.lfoFreq, options.delay, options.depth);

    if (this.audio) {
      this.audio.dispose()
    }
    this.audio = new Tone.Volume(100).connect(Tone.Master);
  }

  startMidi() {
    Tone.start().then(() => {
      Tone.Transport.start()
      const arp = ["C3", "C0", "E3", "C0", "G3", "C0", "B4", "C0", "C4", "C0",
        "B4", "C0", "G3", "C0", "E3", "C0"];
      let lastNote;
      const seq = new Tone.Sequence((time, note) => {
        if (note !== "C0") {
          if (this.midiGateTarget) {
            if (typeof this.midiGateTarget.mute !== 'undefined' ) { // audio-in
              this.midiGateTarget.mute = false;
            } else { // adsr
              this.midiGateTarget.triggerAttackRelease('16n', time);
            }
          }
          if (this.midiVOctTargets) {
            this.midiVOctTargets.forEach(target => {
              target.frequency.value = note;
            })
          }
          EventBus.$emit('step', { cmd: 'gate on', note: note });
          lastNote = note;
        } else {
          if (this.midiGateTarget) {
            if (typeof this.midiGateTarget.mute !== 'undefined' ) {
              this.midiGateTarget.mute = true;
            }
          }
          EventBus.$emit('step', { cmd: 'gate off', note: lastNote })
        }
      }, arp, '8n');
      seq.start(0);
    });
  }

  connect(from, to) {
    const patchName = [from, to].join('_');
    const patch = (from, to) => {
      return [from, to].sort().join('_');
    }
    switch (patchName) {
      case patch('midi-gate', 'amp-volume'):
        this.midiGateTarget = this.amp;
        break
      case patch('midi-gate', 'adsr-gate'):
        this.midiGateTarget = this.adsr;
        break
      case patch('midi-v-oct', 'osc-v-oct'):
        this.midiVOctTargets = [this.oscSaw, this.oscSine, this.oscSquare];
        break

      case patch('osc-saw', 'amp-in'):
        this.oscSaw.connect(this.amp);
        break
      case patch('osc-saw', 'audio-in'):
        this.oscSaw.connect(this.audio);
        break
      case patch('osc-saw', 'filter-in'):
        this.oscSaw.connect(this.filter);
        break
      case patch('osc-saw', 'chorus-in'):
        this.oscSaw.connect(this.chorus);
        break

      case patch('osc-square', 'amp-in'):
        this.oscSquare.connect(this.amp);
        break
      case patch('osc-square', 'audio-in'):
        this.oscSquare.connect(this.audio);
        break
      case patch('osc-square', 'filter-in'):
        this.oscSquare.connect(this.filter);
        break
      case patch('osc-square', 'chorus-in'):
        this.oscSquare.connect(this.chorus);
        break

      case patch('osc-sine', 'amp-in'):
        this.oscSaw.connect(this.amp);
        break
      case patch('osc-sine', 'audio-in'):
        this.oscSine.connect(this.audio);
        break
      case patch('osc-sine', 'filter-in'):
        this.oscSine.connect(this.filter);
        break
      case patch('osc-sine', 'chorus-in'):
        this.oscSine.connect(this.chorus);
        break

      case patch('amp-out', 'audio-in'):
        this.amp.connect(this.audio);
        break

      case patch('filter-out', 'audio-in'):
        this.filter.connect(this.audio);
        break
      case patch('filter-out', 'amp-in'):
        this.filter.connect(this.amp);
        break
      case patch('filter-out', 'chorus-in'):
        this.filter.connect(this.chorus);
        break

      case patch('adsr-out', 'amp-volume'):
        this.adsr._sig.connect(this.amp.gain);
        break

      case patch('chorus-out', 'audio-in'):
        this.chorus.connect(this.audio);
        break
      case patch('chorus-out', 'amp-in'):
        this.chorus.connect(this.amp);
        break
      case patch('chorus-out', 'filter-in'):
        this.chorus.connect(this.filter);
        break
    }
  }
}

export default Synth
